import { ToolResult } from "./developerTools";



/** Output formats supported by the random byte generator. */
export type RandomByteEncoding = "base64" | "hex" | "base64url";

/** Maximum byte count accepted to keep generation and rendering bounded. */
export const MAXIMUM_RANDOM_BYTES = 4096;



/**
 * Creates cryptographically secure bytes using the browser's random number generator.
 * @param length Whole number of bytes from 1 through MAXIMUM_RANDOM_BYTES.
 * @returns Raw random bytes, or a validation or browser availability message.
 */
export const generateRandomBytes = (length: number) : ToolResult<Uint8Array> => {
	if(!Number.isSafeInteger(length) || length < 1 || length > MAXIMUM_RANDOM_BYTES) return { success: false, error: `Enter a whole number of bytes from 1 to ${ MAXIMUM_RANDOM_BYTES }.` };
	try {
		const bytes = new Uint8Array(length);
		window.crypto.getRandomValues(bytes);
		return { success: true, value: bytes };
	} catch {
		return { success: false, error: "Secure random generation is unavailable in this browser. Try a browser that supports Web Crypto." };
	}
};

/**
 * Encodes raw bytes without interpreting them as Unicode text or changing them.
 * @param bytes Bytes produced by the random byte generator.
 * @param encoding Output format; Base64URL omits padding.
 * @returns Encoded representation of the supplied bytes.
 */
export const encodeRandomBytes = (bytes: Uint8Array, encoding: RandomByteEncoding) : string => {
	if(encoding === "hex") return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
	const base64 = btoa(Array.from(bytes, (byte) => String.fromCharCode(byte)).join(""));
	return encoding === "base64url" ? base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") : base64;
};
